/**
 * Professional services (/service-engagements): request a valuation, consulting,
 * analysis, or property or facilities management from a real-estate
 * professional and follow it through delivery. Guests see a preview of the
 * services and a sign-in prompt. Used through useTranslation('engage').
 *
 * The service names follow the existing vocabulary (roleCatalog and the
 * professionals directory).
 */
const engage = {
  title: 'Professional services',
  subtitle:
    'Request a property appraisal, consulting, analysis, or property or facilities management from a real-estate professional, and follow it through delivery.',
  request: 'Request a service',
  cancel: 'Cancel',

  types: {
    APPRAISAL: 'Property appraisal',
    CONSULTING: 'Real-estate consulting',
    ANALYSIS: 'Real-estate analysis',
    PROPERTY_MANAGEMENT: 'Property management',
    FACILITIES_MANAGEMENT: 'Facilities management',
  },

  typeDescriptions: {
    APPRAISAL: "An estimate of your property's value from an appraiser.",
    CONSULTING: 'Expert advice before you buy, sell or invest.',
    ANALYSIS: 'A study of the market, a property or a specific project.',
    PROPERTY_MANAGEMENT: 'Letting your property and looking after its tenants.',
    FACILITIES_MANAGEMENT: 'Maintaining the building and running its facilities.',
  },

  statuses: {
    REQUESTED: 'Requested',
    QUOTED: 'Quoted',
    ACCEPTED: 'Accepted',
    IN_PROGRESS: 'In progress',
    DELIVERED: 'Delivered',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
  },

  advance: {
    QUOTED: 'Mark as quoted',
    ACCEPTED: 'Mark as accepted',
    IN_PROGRESS: 'Start work',
    DELIVERED: 'Deliver the work',
    COMPLETED: 'Mark as completed',
  },

  guest: {
    title: 'Sign in to request a professional service',
    body: 'Send your request to a real-estate professional and follow it in one place, from request to delivery.',
    servicesHeading: 'Available services',
    howHeading: 'How it works',
    steps: {
      choose: 'Choose the service and the professional, and describe what you need.',
      review: 'The professional reviews your request and updates its status: quoted, accepted, then in progress.',
      deliver: 'Follow it here through delivery, with a link to the delivered file if the professional adds one.',
    },
    signIn: 'Sign in',
    createAccount: 'Create an account',
    browse: 'Browse professionals',
  },

  tabs: {
    label: 'Show requests',
    mine: 'My requests',
    incoming: 'Incoming requests',
    withCount: '{{label}} ({{n}})',
  },

  form: {
    heading: 'New service request',
    type: 'Service type',
    typePlaceholder: 'Choose a service type',
    typeRequired: 'Choose a service type.',
    title: 'Request title',
    titlePlaceholder: 'e.g. Appraise an apartment before selling it',
    titleHint: 'A short title for your request, at least 3 characters.',
    titleTooShort: 'Write a title of at least 3 characters.',
    fee: 'Proposed fee in riyals (optional)',
    feeHint: 'A whole number, digits only.',
    description: 'Details (optional)',
    descriptionPlaceholder: 'Location, property type and exactly what you need.',
    submit: 'Send request',
    sent: 'Your request was sent to the professional.',
  },

  provider: {
    label: 'Professional',
    placeholder: 'Search by name or city',
    chooseTypeFirst: 'Choose a service type first',
    none: 'No professionals are listed for this service yet.',
    noMatch: 'No matching professional.',
    loadError: "Couldn't load the list of professionals. Enter the professional's activity ID instead.",
    unnamed: 'Real-estate professional',
    required: 'Choose the professional.',
    manualToggle: "Have the professional's activity ID? Enter it instead",
    directoryToggle: 'Search the list of professionals',
    idLabel: "Professional's activity ID",
    idHint: "Ask the professional you're working with for it.",
    idInvalid: "That activity ID isn't valid. Check it with the professional.",
  },

  list: {
    loading: 'Loading requests…',
    emptyMine: "You haven't requested a service yet.",
    emptyIncoming: 'No incoming requests for your professional activities.',
  },

  card: {
    fee: 'Fee',
    riyal: 'Saudi riyal',
    deliverable: 'View the delivered file',
    opensInNewTab: '(opens in a new tab)',
    cancelRequest: 'Cancel request',
    cancelConfirm: "Cancel this request? You won't be able to continue it afterwards.",
    cancelYes: 'Yes, cancel it',
    keep: 'Keep it',
    deliverLabel: 'Link to the delivered file (optional)',
    deliverHint: "Paste the full link as it appears in the browser's address bar.",
    deliverInvalid: 'Enter a full, valid link.',
    deliverConfirm: 'Confirm delivery',
    updated: 'The request status was updated.',
  },

  errors: {
    disabled: "Professional services aren't enabled yet.",
    load: "Couldn't load your requests. Check your connection and try again.",
    create: "Couldn't send the request. Please try again.",
    invalid: 'Check the fields, then try again.',
    noProvider: "We couldn't find that professional. Check your choice.",
    update: "Couldn't update the request. Please try again.",
    forbidden: "You don't have permission to update this request.",
    notFound: 'This request is no longer available.',
    session: 'Your session has ended. Sign in again to continue.',
    retry: 'Try again',
  },
};

export default engage;
